import React, { useCallback, useState } from 'react';
import { Box, Grid, TextField, Typography } from '@mui/material';
import { ContactInfo } from '../../hooks/useDocumentCollection';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ContactInfoFormProps {
  value: ContactInfo;
  onChange: (value: ContactInfo) => void;
  errors: Record<string, string>;
}

export const ContactInfoForm: React.FC<ContactInfoFormProps> = ({
  value,
  onChange,
  errors,
}) => {
  // Track which fields have been touched (blurred) for real-time validation
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleFieldChange = (field: keyof ContactInfo) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange({
      ...value,
      [field]: event.target.value,
    });
  };

  const handleBlur = useCallback((field: keyof ContactInfo) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  }, []);

  // Calculate real-time validation errors for touched fields
  const getFieldError = (field: keyof ContactInfo): string | undefined => {
    // If field hasn't been touched, don't show validation error yet
    // unless there's a server-side error passed in props
    if (!touched[field] && !errors[field]) {
      return undefined;
    }

    // Use error from props if available (from form submission)
    if (errors[field]) {
      return errors[field];
    }

    // Real-time validation for touched fields
    switch (field) {
      case 'firstName':
        if (!value.firstName?.trim()) {
          return 'First name is required';
        }
        break;
      case 'lastName':
        if (!value.lastName?.trim()) {
          return 'Last name is required';
        }
        break;
      case 'email':
        if (!value.email?.trim()) {
          return 'Email is required';
        }
        if (!EMAIL_REGEX.test(value.email)) {
          return 'Please enter a valid email address';
        }
        break;
    }

    return undefined;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Contact Information
      </Typography>

      <Grid container spacing={2}>
        {/* First Name and Last Name Row */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={value.firstName}
            onChange={handleFieldChange('firstName')}
            onBlur={handleBlur('firstName')}
            error={Boolean(getFieldError('firstName'))}
            helperText={getFieldError('firstName')}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={value.lastName}
            onChange={handleFieldChange('lastName')}
            onBlur={handleBlur('lastName')}
            error={Boolean(getFieldError('lastName'))}
            helperText={getFieldError('lastName')}
            required
          />
        </Grid>

        {/* Email - Full Width */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={value.email}
            onChange={handleFieldChange('email')}
            onBlur={handleBlur('email')}
            error={Boolean(getFieldError('email'))}
            helperText={getFieldError('email')}
            required
          />
        </Grid>

        {/* Phone and Company Row */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            type="tel"
            value={value.phone || ''}
            onChange={handleFieldChange('phone')}
            error={Boolean(errors.phone)}
            helperText={errors.phone}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Company / Affiliation"
            value={value.company || ''}
            onChange={handleFieldChange('company')}
            error={Boolean(errors.company)}
            helperText={errors.company}
          />
        </Grid>
      </Grid>
    </Box>
  );
};
